/**
 * Creates a radial gauge for a scorecard node using these options:
 *
 * <pre>
 * {
 *     el: // the element that will contain the gauge
 *     model: // the scorecard node
 *     currentScore: // the score in the current period
 *     previousScore: // the score in the previous period
 *     label: // true to indicate the label should be included.  default false
 *     organization: // true to indicate the organization should be included.  default false Will be read from model.organization.
 *     score: // true to indicate the score should be shown below the gauge.  default true
 *     thresholds: // true to indicate the thresholds should be shown below the gauge.  default true
 *     period: // true to indicate the period should be shown. default false
 *     periodId: // the period ID of the data being displayed by the gauge.
 * }
 * </pre>
 */

import Backbone from 'backbone'

import radialGauge from 'impact-radial-gauge'
import formatValue from 'scoreboard/components/format-value'
import { shortenedAndUnshortened } from 'scoreboard/components/format-value'
import _ from 'lodash'
import config from 'scoreboard/components/config'
import i18n from 'scoreboard/components/i18n'
import { higherBetter } from 'scoreboard/components/thresholds-and-colors'
import calendars from 'scoreboard/components/calendars'
import trend from 'scoreboard/components/trend'
import cleanObject from 'scoreboard/components/clean-object'

import {
	title,
	subtitle,
	value,
	raw,
	thresholds,
	colors,
	createNonAbbreviatedTooltip,
} from './gauge'

import ScorecardNode from 'scoreboard/scorecards/scorecard-node'
const { metricish } = ScorecardNode
import globals from 'scoreboard/components/globals'
import TooltipAndDetails from 'scoreboard/components/tooltip-and-details'
import constants from 'scoreboard/components/constants'
const { UNSCORED, UNSCORED_TEXT, NUMBER, STABILIZE_NUMBER } = constants
import DataType from 'scoreboard/datasets/model/data-type'
const { YES_NO } = DataType

var debug = false

var Radial = Backbone.View.extend({

	className: 'radial-gauge',

	initialize: function(options) {
		this.options = options
		// standardize model format as plain object, not backbone model
		this.options.model = options.model.attributes || options.model
	},

	render: function(options) {
		var opt = _.extend({
			score: true,
			thresholds: 'default',
			value: true
		}, this.options, options)

		if (debug) {
			console.log(JSON.stringify(opt, null, '    '))
		}

		// note: the radial gauge needs this.el in the DOM to support word wrapping in SVGs.
		// scorecards-overview and dashboards/display/display.js both ensure this by
		// adding the element to the DOM before calling render()
		this.data = data(opt)
		console.log(this.data)
		radialGauge(this.el, this.data)

		// Nix any existing non-abbreviated value tooltips
		this._removeTooltips()

		// Creates and returns a nonAbbreviatedValuesTooltip if the given value is to be shortend
		var createValueTooltip = (value, selector, tooltipClass, header) => {
			var { shortened, unshortened } = shortenedAndUnshortened({
				value: value.unformatted || value.raw,
				precision: this.options.model.precision,
				dataType: this.options.model.dataType,
				currencyCode: this.options.model.currencyCode
			})

			if (shortened != unshortened) {
				return createNonAbbreviatedTooltip(
					this.el,
					selector,
					tooltipClass + ' ' + this.options.tooltipExtraClass,
					header || calendars.periodsById[globals.periodId].label,
					unshortened)
			} else {
				// #24351 provide a plain tooltip to explain speedometer trend
				// for non-abbreviated change
				if (tooltipClass === 'current-change') {
					return new TooltipAndDetails({
						el: this.el,
						selector,
						tooltipOptions: {
							placement: 'above',
							extraClass: 'details-only'
						},
						tooltipContent: () => {
							return header
						}
					})
				}
			}
			return
		}

		// Create new tooltips for the value/change related parts of the radial gauge
		this.currentValueTooltip = this.data.value ?
			createValueTooltip(this.data.value, 'g.header-row .value', 'current-score') : null
		if (this.data.change) {
			var header = i18n.get('dashboards.speedometer.changeFromCustom',
				calendars.safeRelativePeriod(globals.periodId, -1).label)
			this.currentChangeTooltip = createValueTooltip(this.data.change,
				'g.header-row g.change text', 'current-change', header)
		}

		let [fieldsMinValue, ,fieldsMaxValue] = this.data.fields || []

		if (this.data.fields && fieldsMinValue &&
				fieldsMinValue.formatted != fieldsMinValue.unshortened) {
			this.minValueTooltip = createNonAbbreviatedTooltip(
				this.el,
				'g.field:first-child text.value',
				'gauge-value ' + this.options.tooltipExtraClass,
				fieldsMinValue.label,
				fieldsMinValue.unshortened)
		}

		if (this.data.fields && fieldsMaxValue &&
				fieldsMaxValue.formatted != fieldsMaxValue.unshortened) {
			this.maxValueTooltip = createNonAbbreviatedTooltip(
				this.el,
				'g.field:last-child text.value',
				'gauge-value ' + this.options.tooltipExtraClass,
				fieldsMaxValue.label,
				fieldsMaxValue.unshortened)
		}

		this.trigger('done')
		return this
	},

	_removeTooltips: function() {
		_.invokeMap([this.currentValueTooltip,
			this.currentChangeTooltip, this.minValueTooltip,
			this.maxValueTooltip], 'remove')
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		this._removeTooltips()
	}
})

function data(opt) {
	return cleanObject({
		title: title(opt),
		subtitle: subtitle(opt),
		// highlightArcThreshold: true is safe for all current scenarios, because
		// things like unscored metrics and those with missing data don't have an
		// arc to highlight
		// https://github.com/SpiderStrategies/Scoreboard/issues/18960#issuecomment-445033635
		highlightArcThreshold: true,
		explanation: explanation(opt),
		value: value(opt),
		change: change(opt),
		colors: colors(opt),
		thresholds: thresholds(opt),
		fields: fields(opt),
		hoverText: opt.hoverText,
		// this is correct: it's units for radial-gauge, but unit in ApiScorecardNode
		units: opt.model.unit
	})
}

function change(opt) {
	var change
	if (opt.model.dataType === YES_NO) {
		change = null
	// #17888 Don't show score trend when scores are turned off
	} else if (!metricish(opt.model) && !config.scoresVisible) {
		change = null
	} else {
		change = trend(opt.model, opt.previousScore, opt.currentScore)
		if (change.direction === 'none') {
			change = null
		}
	}
	return change
}

/**
 * Returns the explanation String, if appropriate.  For example,
 * 'There is no data for this period'
 */
function explanation(opt) {
	if (opt.hideExplanation || !_.isNil(opt.datasetQueryResponse)) {
		return null
	}

	if (opt.currentScore && opt.currentScore.color === 'infantry-blue' && config.replicationOutputEnabled) {
		return i18n.get('scorecards.node.metric.classifieddata')
	}

	if (metricish(opt.model) && false === _.result(opt, 'currentScore.applicable')) {
		return i18n.get('scorecards.node.metric.notapplicable.explanation')
	}
	var r = raw(opt)
	if (r == null || _.isUndefined(r)) {
		return i18n.get('scorecards.node.metric.nodata')
	}

	return null
}

/**
 * Returns the array of text field objects at the bottom of the speedometer,
 * e.g. [ { label: 'Score', formatted: '5' }, { label: 'Goal', formatted: '5' } ]
 */
function fields(opt) {
	let scoringType = config.scoringType(opt.model.scoringTypeId)

	if (_.get(scoringType, 'kpiValueType') == UNSCORED_TEXT) {
		return null
	}

	if (!opt.currentScore ||
		_.get(scoringType, 'standardScoringType') == UNSCORED ||
		!metricish(opt.model) && opt.currentScore.score == null) {
		return null
	}

	let thresholds = opt.currentScore.thresholds
	var f = {}

	// add the score
	if (opt.score && config.scoresVisible) {
		f.middle = {
			label: i18n.get('common.score'),
			formatted: formatValue({
				value: opt.currentScore.score,
				dataType: 'score'
			}) || NO_VALUE
		}
	}

	// Only check/build threshold fields for metrics
	if (metricish(opt.model)) {
		let leftThreshold, leftThresholdLabel = null
		if (opt.thresholds === 'default') {
			switch (_.get(scoringType, 'standardScoringType')) {
				case constants.GOAL_RED_FLAG:
				case constants.SCORED_MIDDLE_4_COLOR:
					leftThreshold = _.nth(thresholds, 0)
					leftThresholdLabel = 'scorecards.node.metric.threshold.base'
					break
				case constants.THREE_COLOR:
				case constants.FOUR_COLOR_ORANGE:
				case constants.FOUR_COLOR_BLUE:
					leftThreshold = _.nth(thresholds, 1)
					leftThresholdLabel = 'scorecards.node.metric.threshold.base'
					break
			}
		} else if (opt.thresholds === 'best-worst') {
			leftThreshold = _.nth(thresholds, 0)
			leftThresholdLabel = 'scorecards.node.metric.threshold.worst'
		}

		if (leftThresholdLabel != null) {
			f.left = thresholdField(opt.model, leftThreshold, leftThresholdLabel)
		}

		let rightThreshold, rightThresholdLabel = null
		if (opt.thresholds === 'default') {
			// Default right threshold for all metric types is "goal", though its
			// index and label varies

			let scoringType = config.scoringType(opt.model.scoringTypeId)
			if (_.isNumber(scoringType.goalIndex) && !scoringType.isCustomText) {
				rightThreshold = _.nth(thresholds, scoringType.goalIndex)
				if (_.includes([NUMBER, STABILIZE_NUMBER], scoringType.kpiValueType)) {
					rightThresholdLabel = i18n.get('scorecards.node.metric.threshold.goal')
				} else {
					rightThresholdLabel = scoringType.thresholdNames[scoringType.goalIndex]
				}
			}
		} else if (opt.thresholds === 'best-worst') {
			rightThreshold = _.get(scoringType, 'standardScoringType') === constants.THREE_COLOR
				? _.nth(thresholds, 3)
				: _.nth(thresholds, 4)
			rightThresholdLabel = 'scorecards.node.metric.threshold.best'
		}

		if (rightThresholdLabel != null) {
			f.right = thresholdField(opt.model, rightThreshold, rightThresholdLabel)
		}
	}

	if (!(f.left || f.middle || f.right)) {
		return []
	}

	// insert a blank object for any missing fields so that the space is still occupied
	var arr = [
		f.left || {},
		f.middle || {},
		f.right || {}
	]

	if (!config.speedometersRedOnLeft && !higherBetter(opt)
			&& !(arr[0].formatted === NO_VALUE && arr[2].formatted == NO_VALUE)) {
		arr.reverse()
	}

	if (metricish(opt.model) && opt.currentScore && opt.currentScore.applicable === false) {
		arr.forEach(f => {
			if (f && f.formatted) {
				f.unshortened = f.formatted = i18n.get('scorecards.node.metric.notapplicable')
			}
		})
	}

	return arr
}

function thresholdField (model, thresholdValue, thresholdLabel) {
	let {shortened, unshortened} = shortenedAndUnshortened({
		value: thresholdValue,
		dataType: model.dataType,
		precision: model.precision,
		currencyCode: model.currencyCode
	})

	let key = thresholdLabel + '.short'
	let label = i18n.get(key)
	// if there wasn't anything in the resource bundle for '.short' the user
	// probably typed in the name, so the label we use is going to need to be
	// without '.short' on the end
	if (label == key) {
		label = i18n.get(thresholdLabel)
	}

	return {
		label,
		formatted: shortened || NO_VALUE,
		unshortened: unshortened || NO_VALUE
	}
}

var NO_VALUE = '-'

_.extend(Radial, {
	data,
	explanation,
	title,
	subtitle,
	fields,
	value,
	colors,
	thresholds
})
export default Radial
export { data }
export { explanation }
export { title }
export { subtitle }
export { fields }
export { value }
export { colors }
export { thresholds }

Radial.__filename__ = 'components/gauge/radial.js'
